import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Users,
  Settings,
  BarChart3,
  FileText,
  UserCog,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Bell,
  CreditCard,
  Shield,
  Database,
  Activity,
  Calendar,
  Mail,
  Globe,
  Zap,
  HelpCircle,
  Search,
  Upload,
  Download,
  Archive,
  Monitor,
  Smartphone,
  Key,
  IndentIcon,
  FormInput,
  LogOut,
  Building,
  Landmark,
  Gift,
  User,
  Tags,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils';
import { useCurrentAdmin, useUserRole } from '@/hooks/redux';
import { ACCOUNT_TYPES, UserRole } from '@/types';
import { useLogoutMutation } from '@/store/api/authApi';
import { useDispatch } from 'react-redux';
import { clearAuth } from '@/store';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavItem {
  name: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  badge?: string;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  // {
  //   name: 'Analytics',
  //   icon: BarChart3,
  //   children: [
  //     { name: 'Overview', href: '/analytics', icon: Activity },
  //     { name: 'Reports', href: '/analytics/reports', icon: FileText },
  //     { name: 'Real-time', href: '/analytics/realtime', icon: Monitor },
  //     { name: 'Performance', href: '/analytics/performance', icon: Zap },
  //   ],
  // },
  {
    name: 'User Management',
    icon: Users,
    adminOnly: true,
    children: [
      { name: 'Investors', href: `/users/${ACCOUNT_TYPES.INVESTOR}`, icon: Users },
      { name: 'Asset Owner', href: `/users/${ACCOUNT_TYPES.ASSETOWNER}`, icon: Users },
      { name: 'Company', href: `/users/${ACCOUNT_TYPES.COMPANY}`, icon: Users },
      { name: 'Broker', href: `/users/${ACCOUNT_TYPES.BROKER}`, icon: Users },
      // { name: 'User Roles', href: '/users/roles', icon: Shield },
      // { name: 'Permissions', href: '/users/permissions', icon: Key },
      // { name: 'User Groups', href: '/users/groups', icon: Archive },
    ],
  },
  {
    name: 'Bank',
    icon: Landmark,
    adminOnly: true,
    children: [
      { name: 'Accounts', href: '/bank-account', icon: Users },
      { name: 'UPI', href: '/upi', icon: Users },
    ],
  },
  {
    name: 'Transactions',
    icon: FileText,
    children: [
      { name: 'Deposit Request', href: '/deposit-request', icon: FileText },
      { name: 'Withdraw Request', href: '/withdraw-request', icon: Upload },
    ],
  },
  {
    name: 'Kyc Request',
    icon: Shield,
    children: [
      { name: 'Trustee KYC', href: '/trustee-kyc-request', icon: IndentIcon },
      { name: 'User KYC', href: '/user-kyc-request', icon: Shield },
      { name: 'Company KYC', href: '/company-kyc-request', icon: Building },
    ],
  },
  // { name: 'Master RWA Form', href: '/master-rwa-form', icon: FormInput },
  { name: 'Asset List', href: '/asset-list', icon: Building },
  { name: 'Coupon', href: '/coupon-page', icon: Gift },
  { name: 'Vendor List', href: '/vendors-page', icon: User },
  { name: 'Category', href: '/category', icon: Tags },
  { name: 'Groups', href: '/groups', icon: Users },

  // {
  //   name: 'E-commerce',
  //   icon: CreditCard,
  //   children: [
  //     { name: 'Products', href: '/ecommerce/products', icon: Archive },
  //     { name: 'Orders', href: '/ecommerce/orders', icon: CreditCard, badge: '12' },
  //     { name: 'Customers', href: '/ecommerce/customers', icon: Users },
  //     { name: 'Inventory', href: '/ecommerce/inventory', icon: Database },
  //   ],
  // },
  // {
  //   name: 'Communication',
  //   icon: Mail,
  //   children: [
  //     { name: 'Messages', href: '/communication/messages', icon: Mail, badge: '5' },
  //     { name: 'Notifications', href: '/communication/notifications', icon: Bell },
  //     { name: 'Email Templates', href: '/communication/templates', icon: FileText },
  //     { name: 'Broadcasts', href: '/communication/broadcasts', icon: Globe },
  //   ],
  // },
  // { name: 'Calendar', href: '/calendar', icon: Calendar },

  { name: 'Security', href: '/change-password', icon: Shield },

  // {
  //   name: 'Tools',
  //   icon: Settings,
  //   children: [
  //     { name: 'Search', href: '/tools/search', icon: Search },
  //     { name: 'Import/Export', href: '/tools/import-export', icon: Download },
  //     { name: 'Backup', href: '/tools/backup', icon: Database },
  //     { name: 'Logs', href: '/tools/logs', icon: FileText },
  //   ],
  // },
  { name: 'Profile', href: '/profile', icon: UserCog },
  // {
  //   name: 'Settings',
  //   icon: Settings,
  //   children: [
  //     { name: 'General', href: '/settings', icon: Settings },
  //     { name: 'Security', href: '/settings/security', icon: Shield },
  //     { name: 'Notifications', href: '/settings/notifications', icon: Bell },
  //     { name: 'Integrations', href: '/settings/integrations', icon: Zap },
  //     { name: 'API Keys', href: '/settings/api', icon: Key },
  //     { name: 'Billing', href: '/settings/billing', icon: CreditCard },
  //   ],
  // },
  // { name: 'Help & Support', href: '/support', icon: HelpCircle },
];

interface NavItemComponentProps {
  item: NavItem;
  isOpen: boolean;
  level?: number;
}

function NavItemComponent({ item, isOpen, level = 0 }: NavItemComponentProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const userRole = useUserRole();
  const adminDetail = useCurrentAdmin();

  const isAdmin = userRole === UserRole.ADMIN;

  if (item.adminOnly && !isAdmin) {
    return null;
  }

  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.href ? location.pathname === item.href : false;
  const hasActiveChild = item.children?.some(
    (child) => child.href && location.pathname.startsWith(child.href)
  );

  // function NavItemComponent({ item, isOpen, level = 0 }: NavItemComponentProps) {
  //   const [isExpanded, setIsExpanded] = useState(false);
  //   const location = useLocation();
  //   const userRole = useUserRole();
  //   const adminDetail = useCurrentAdmin();

  //   const isAdmin = userRole === UserRole.ADMIN;

  //   if (item.adminOnly && !isAdmin) {
  //     return null;
  //   }

  //   const hasChildren = item.children && item.children.length > 0;

  //   // from new 4 update (Permanent Fix for Highlighting)
  //   const currentPath = location.pathname;

  //   // 1. Check if we are in any sub-detail page
  //   const isUserDetail = currentPath.startsWith('/user-detail/');
  //   const isLeadDetail = currentPath.startsWith('/admin/broker/lead-details/');

  //   // 2. Exact Match for normal navigation
  //   let isActive = item.href ? currentPath === item.href : false;

  //   // 3. Persistent Highlight Logic
  //   if (item.name === 'Investors' && (isUserDetail || isLeadDetail)) {
  //     // Agar URL mein Investor word hai ya hum detail mein hain, toh ise active rakho
  //     // Note: Detail page par agar specific logic chahiye toh hum query check kar sakte hain
  //     // Filhal hum ise active rakh rahe hain agar path detail wala hai
  //     if (currentPath.includes('Investor') || isUserDetail || isLeadDetail) isActive = true;
  //   }

  //   // Generic condition for all User Management tabs
  //   const isManagementTab = item.href?.includes('/users/');
  //   if (isManagementTab && (isUserDetail || isLeadDetail)) {
  //      // Agar management tab ka href current type se match karta hai (e.g. BROKER in URL)
  //      const typeInHref = item.href?.split('/').pop(); // e.g. 'BROKER'
  //      // Hum check karte hain ki kya ye detail page usi type ka hai
  //      // Agar aap detail page ke URL mein type nahi bhej rahe, toh hum last visited type use kar sakte hain
  //      if (typeInHref && document.referrer.includes(typeInHref)) isActive = true;
  //   }

  //   const hasActiveChild = item.children?.some(
  //     (child) => child.href && (currentPath.startsWith(child.href) || (isUserDetail && child.href.includes('/users/')))
  //   );
  // to new 4 update

  // Auto-expand if has active child
  React.useEffect(() => {
    if (hasActiveChild && isOpen) {
      setIsExpanded(true);
    }
  }, [hasActiveChild, isOpen]);

  const handleClick = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  const Icon = item.icon;
  const indent = level * 16;

  if (hasChildren) {
    return (
      <div>
        <button
          aria-label="up-down"
          onClick={handleClick}
          className={cn(
            'flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            isActive || hasActiveChild
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            !isOpen && 'justify-center'
          )}
          style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
          title={!isOpen ? item.name : undefined}
        >
          <Icon className={cn('h-5 w-5', isOpen && 'mr-3')} />
          {isOpen && (
            <>
              <span className="flex-1 text-left">{item.name}</span>
              {item.badge && (
                <span className="ml-2 inline-flex items-center rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium">
                  {item.badge}
                </span>
              )}
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 ml-2" />
              ) : (
                <ChevronDown className="w-4 h-4 ml-2" />
              )}
            </>
          )}
        </button>

        {/* Submenu */}
        {hasChildren && isExpanded && isOpen && (
          <div className="mt-1 space-y-1">
            {item.children!.map((child) => (
              <NavItemComponent key={child.name} item={child} isOpen={isOpen} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Regular nav item
  return (
    <NavLink
      to={item.href!}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          !isOpen && 'justify-center'
        )
      }
      style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
      title={!isOpen ? item.name : undefined}
    >
      <Icon className={cn('h-5 w-5', isOpen && 'mr-3')} />
      {isOpen && (
        <>
          <span>{item.name}</span>
          {item.badge && (
            <span className="ml-auto inline-flex items-center rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium">
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export function EnhancedSidebar({ isOpen, onToggle }: SidebarProps) {
  const adminDetail = useCurrentAdmin();
  const userRole = useUserRole();
  const [logoutApi] = useLogoutMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logoutApi().unwrap();
    } catch (error) {
      console.error('Logout API failed:', error);
    } finally {
      dispatch(clearAuth());
      navigate('/login', { replace: true });
    }
  };

  return (
    <>
      {/* Sidebar backdrop for mobile */}
      {isOpen && <div className="fixed inset-0 z-20 bg-black/50 lg:hidden" onClick={onToggle} />}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex flex-col bg-card border-r transition-all duration-300 overflow-hidden',
          isOpen ? 'w-64 translate-x-0' : 'w-16 -translate-x-full lg:translate-x-0'
        )}
      >
        {/* Sidebar header */}
        <div className="flex items-center h-16 px-4 border-b">
          <div className={cn('flex items-center', isOpen ? 'w-full' : 'justify-center')}>
            <div className="flex items-center justify-center w-8 h-8 font-bold rounded-lg bg-primary text-primary-foreground">
              <img src="/smallLogo.svg" alt="" />
            </div>
            {isOpen && (
              <div className="flex-1 ml-3">
                <span className="text-lg font-semibold">Admin</span>
              </div>
            )}
          </div>

          {/* Toggle button - desktop only */}
          <Button
            aria-label="Left Right"
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className={cn('hidden lg:flex h-6 w-6', isOpen ? 'ml-auto' : 'fixed left-2 top-3 z-40')}
          >
            {isOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto sidebar">
          {navigation.map((item) => (
            <NavItemComponent key={item.name} item={item} isOpen={isOpen} />
          ))}
        </nav>

        {/* User info */}
        {isOpen && adminDetail && (
          <div className="p-4 border-t">
            <div className="flex items-center">
              {adminDetail?.profileImage ? (
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground">
                  <img
                    src={adminDetail?.profileImage}
                    alt="Admin profile"
                    className="object-cover w-full h-full rounded-full"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center w-8 h-8 text-sm rounded-full bg-primary text-primary-foreground">
                  {adminDetail?.firstname.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0 ml-3">
                <p className="text-sm font-medium truncate">{adminDetail?.firstname}</p>
                <p className="text-xs truncate text-muted-foreground">
                  {userRole?.toLowerCase()} • {adminDetail?.email}
                </p>
              </div>
            </div>

            {/* Quick actions */}
            <div className="flex mt-3 space-x-2">
              <Button
                onClick={handleLogout}
                variant="outline"
                aria-label="Logout "
                size="sm"
                className="flex-1"
              >
                <LogOut className="w-3 h-3 mr-1" />
                Logout
              </Button>
            </div>
          </div>
        )}

        {/* Collapsed user indicator */}
        {!isOpen && adminDetail && (
          <div className="p-2 border-t">
            <div className="flex justify-center">
              <div className="flex items-center justify-center w-8 h-8 text-sm rounded-full bg-primary text-primary-foreground">
                {adminDetail?.firstname.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
